import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    MenuItem,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from "@mui/material";
import { Add, Close, DeleteOutline, Edit, Refresh } from "@mui/icons-material";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";
import {
    listAwsSesAccounts,
    createAwsSesAccount,
    updateAwsSesAccount,
    deleteAwsSesAccount,
} from "../../../api/superAdminApi";

const EMPTY_FORM = {
    name: "",
    aws_access_key_id: "",
    aws_secret_access_key: "",
    aws_region: "us-east-1",
    smtp_username: "",
    smtp_password: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_secure: "tls",
    status: "ACTIVE",
};

const AdminAwsSesAccountsPage = () => {
    const notify = useNotification();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { accounts: rows } = await listAwsSesAccounts({ per_page: 100 });
            setAccounts(rows);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (account) => {
        setEditingId(account.id);
        setForm({
            name: account.name || "",
            aws_access_key_id: "",
            aws_secret_access_key: "",
            aws_region: account.aws_region || "us-east-1",
            smtp_username: account.smtp_username || "",
            smtp_password: "",
            smtp_host: account.smtp_host || "",
            smtp_port: account.smtp_port ?? 587,
            smtp_secure: account.smtp_secure || "tls",
            status: account.status || "ACTIVE",
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            setError("Account name is required.");
            return;
        }
        if (!editingId && (!form.aws_access_key_id || !form.aws_secret_access_key)) {
            setError("AWS access key and secret are required for new accounts.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const payload = {
                name: form.name.trim(),
                aws_region: form.aws_region || undefined,
                smtp_username: form.smtp_username || undefined,
                smtp_host: form.smtp_host || undefined,
                smtp_port: form.smtp_port ? Number(form.smtp_port) : undefined,
                smtp_secure: form.smtp_secure || undefined,
                status: form.status,
            };
            if (form.aws_access_key_id) payload.aws_access_key_id = form.aws_access_key_id;
            if (form.aws_secret_access_key) payload.aws_secret_access_key = form.aws_secret_access_key;
            if (form.smtp_password) payload.smtp_password = form.smtp_password;

            if (editingId) {
                await updateAwsSesAccount(editingId, payload);
                notify("AWS SES account updated", "success");
            } else {
                await createAwsSesAccount(payload);
                notify("AWS SES account created", "success");
            }
            setDialogOpen(false);
            await load();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this AWS SES account? Domains linked to it must be removed first.")) return;
        try {
            await deleteAwsSesAccount(id);
            notify("AWS SES account deleted", "success");
            await load();
        } catch (err) {
            notify(err.message, "error");
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ ...WB_SX.headerRow, mb: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ ...WB_SX.pageTitle, borderLeft: "4px solid #6d28d9", pl: 1.5 }}>
                        AWS SES accounts
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720, lineHeight: 1.65 }}>
                        Manage AWS credentials used when users create email domains. At least one ACTIVE account is required before domains can be provisioned.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>
                        Refresh
                    </Button>
                    <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={WB_SX.primaryButton}>
                        Add account
                    </Button>
                </Box>
            </Box>

            {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert> : null}

            <Paper elevation={0} sx={{ ...WB_SX.card, overflow: "hidden" }}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {["Name", "Region", "SMTP user", "Status", ""].map((h) => (
                                        <TableCell key={h || "actions"} sx={WB_SX.tableHeadCell}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {accounts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">No AWS SES accounts configured.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    accounts.map((row) => (
                                        <TableRow key={row.id} sx={WB_SX.tableRow}>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{row.aws_region || "—"}</TableCell>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{row.smtp_username || "—"}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={row.status} color={row.status === "ACTIVE" ? "success" : "default"} variant="outlined" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteOutline fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {editingId ? "Edit AWS SES account" : "Add AWS SES account"}
                    <IconButton onClick={() => setDialogOpen(false)} disabled={saving}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="AWS access key ID" value={form.aws_access_key_id} onChange={(e) => setForm((f) => ({ ...f, aws_access_key_id: e.target.value }))} placeholder={editingId ? "Leave blank to keep" : ""} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="AWS secret access key" type="password" value={form.aws_secret_access_key} onChange={(e) => setForm((f) => ({ ...f, aws_secret_access_key: e.target.value }))} placeholder={editingId ? "Leave blank to keep" : ""} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="AWS region" value={form.aws_region} onChange={(e) => setForm((f) => ({ ...f, aws_region: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select fullWidth size="small" label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="SMTP username" value={form.smtp_username} onChange={(e) => setForm((f) => ({ ...f, smtp_username: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="SMTP password" type="password" value={form.smtp_password} onChange={(e) => setForm((f) => ({ ...f, smtp_password: e.target.value }))} placeholder={editingId ? "Leave blank to keep" : ""} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="SMTP host" value={form.smtp_host} onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth size="small" label="SMTP port" type="number" value={form.smtp_port} onChange={(e) => setForm((f) => ({ ...f, smtp_port: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField select fullWidth size="small" label="Security" value={form.smtp_secure} onChange={(e) => setForm((f) => ({ ...f, smtp_secure: e.target.value }))}>
                                <MenuItem value="tls">TLS</MenuItem>
                                <MenuItem value="ssl">SSL</MenuItem>
                                <MenuItem value="">None</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
                        {editingId ? "Save changes" : "Create account"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminAwsSesAccountsPage;
